/** Debounce input and invalidate previous responses immediately, including before the next request. */
export function createPreviewScheduler<T, R>(options: {
  load: (input: T, signal: AbortSignal) => Promise<R>
  pending: () => void
  resolved: (result: R) => void
  rejected: (error: unknown) => void
  delay?: number
}) {
  let timer: ReturnType<typeof setTimeout> | undefined
  let controller: AbortController | undefined
  let revision = 0

  function cancel() {
    revision++
    clearTimeout(timer)
    controller?.abort()
    controller = undefined
  }

  function schedule(input: T) {
    cancel()
    const current = revision
    options.pending()
    timer = setTimeout(async () => {
      const request = new AbortController()
      controller = request
      try {
        const result = await options.load(input, request.signal)
        if (current === revision && !request.signal.aborted) options.resolved(result)
      } catch (error) {
        if (current === revision && !request.signal.aborted) options.rejected(error)
      }
    }, options.delay ?? 500)
  }

  return { schedule, cancel }
}
